// src/api/services/organizations.service.ts

import axiosInstance from '../axios.config';
import { ApiResponse } from 'types';
import { mockOrganizations } from 'mocks/mockServer';

// Use mock data flag
const USE_MOCK_DATA = process.env.REACT_APP_USE_MOCK === 'true';

export interface Organization {
  id: string;
  name: string;
  type: 'SERVICE' | 'BUSINESS';
  businessId: string;
  parentOrganizationId?: string;
  description?: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  /** Whether this organisation may place outbound calls (master-admin granted). */
  outboundCallingEnabled?: boolean;
}

export interface CreateBusinessRequest {
  businessName: string;
  businessDescription?: string;
  businessId: string;
  serviceProviderId: string;
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
  adminPassword: string;
  adminPhone?: string;
}

export interface CreateServiceProviderRequest {
  serviceProviderName: string;
  serviceProviderDescription?: string;
  serviceProviderId: string;
  superAdminFirstName: string;
  superAdminLastName: string;
  superAdminEmail: string;
  superAdminPassword: string;
  superAdminPhone?: string;
}

export const organizationsService = {
  /**
   * Create a new service provider organization with super admin user
   */
  createServiceProvider: async (data: CreateServiceProviderRequest): Promise<ApiResponse<Organization>> => {
    const response = await axiosInstance.post<ApiResponse<Organization>>(
      '/organizations/service',
      data
    );
    return response.data;
  },

  /**
   * Create a new business organization with admin user
   */
  createBusiness: async (data: CreateBusinessRequest): Promise<ApiResponse<Organization>> => {
    if (USE_MOCK_DATA) {
      return mockOrganizations.createBusiness(data);
    }
    const response = await axiosInstance.post<ApiResponse<Organization>>(
      '/organizations/business',
      data
    );
    return response.data;
  },

  /**
   * Get all service providers
   */
  /**
   * A business allows or withdraws its service provider's access.
   *
   * Only the business itself can call this — a provider re-enabling its own access
   * would make the control decoration, consent restored by the party it was withdrawn
   * from. The server refuses and says so; the UI does not have to guess.
   */
  async setProviderAccess(organizationId: string, allowed: boolean): Promise<ApiResponse<any>> {
    try {
      const res = await axiosInstance.patch(`/organizations/${organizationId}/provider-access`, { allowed });
      return { success: res.data.success, data: res.data.data, message: res.data.message };
    } catch (e: any) {
      return { success: false, error: e.response?.data?.message || 'Could not change provider access' };
    }
  },

  getServiceProviders: async (): Promise<ApiResponse<Organization[]>> => {
    if (USE_MOCK_DATA) {
      return mockOrganizations.getServiceProviders();
    }
    const response = await axiosInstance.get<ApiResponse<Organization[]>>(
      '/organizations/services'
    );
    return response.data;
  },

  /**
   * Get all businesses under a service provider
   */
  getBusinessesByServiceProvider: async (serviceId: string): Promise<ApiResponse<Organization[]>> => {
    if (USE_MOCK_DATA) {
      return mockOrganizations.getBusinessesByServiceProvider(serviceId);
    }
    const response = await axiosInstance.get<ApiResponse<Organization[]>>(
      `/organizations/services/${serviceId}/businesses`
    );
    return response.data;
  },

  /**
   * Get all businesses (for super admin)
   */
  getAllBusinesses: async (): Promise<ApiResponse<Organization[]>> => {
    if (USE_MOCK_DATA) {
      return mockOrganizations.getAllBusinesses();
    }
    const response = await axiosInstance.get<ApiResponse<Organization[]>>(
      '/organizations/businesses'
    );
    return response.data;
  },

  /**
   * Get organization by ID
   */
  getOrganizationById: async (id: string): Promise<ApiResponse<Organization>> => {
    if (USE_MOCK_DATA) {
      return mockOrganizations.getOrganizationById(id);
    }
    const response = await axiosInstance.get<ApiResponse<Organization>>(
      `/organizations/${id}`
    );
    return response.data;
  },

  /**
   * Update an organization (master/platform admin only). Supports RE-PARENTING:
   * parentOrganizationId = "" moves to root, a UUID moves under that org,
   * undefined leaves the parent unchanged.
   */
  updateOrganization: async (
    id: string,
    data: { name?: string; description?: string; active?: boolean; parentOrganizationId?: string }
  ): Promise<ApiResponse<Organization>> => {
    try {
      const response = await axiosInstance.put<ApiResponse<Organization>>(
        `/organizations/${id}`,
        data
      );
      return response.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to update organization' };
    }
  },
};
